/**
 * Role filters for the management {@link DataTable}.
 * Each group is a local chip/search block; visibility is allowed ids ∩ visible ids.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { SearchInput } from '@/components/forms/SearchInput';
import { DynamicIcon } from '@/components/forms/IconPicker';
import { ROLE_MODE_CONFIG, ROLE_STATUS_CONFIG } from './role-configs';
import type { RoleFilters } from './role-configs';

/** Props for {@link RoleFiltersContent}. */
interface RoleFiltersContentProps {
  filters: RoleFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  roleGroups: Array<{ code: string; label: string; icon: string | null; color: string | null }>;
  onToggleRoleGroup: (groupCode: string) => void;
  onToggleStatus: (status: string) => void;
  onToggleMode: (mode: string) => void;
  onSetSearchTerm: (term: string) => void;
  onSetHierarchyRange: (min: number | undefined, max: number | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Multi-select chips for role families. */
const RoleGroupFilter: React.FC<{
  filters: RoleFilters;
  roleGroups: Array<{ code: string; label: string; icon: string | null; color: string | null }>;
  onToggleRoleGroup: (groupCode: string) => void;
}> = ({ filters, roleGroups, onToggleRoleGroup }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Familles de rôles</span>
      <div className="filter-options">
        {roleGroups.map((group) => {
          const isActive = filters.roleGroups.includes(group.code);
          return (
            <Button
              key={group.code}
              variant="custom"
              size="sm"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={
                {
                  '--filter-color': group.color || 'var(--color-1-nuance-1)',
                } as React.CSSProperties
              }
              onClick={() => onToggleRoleGroup(group.code)}
            >
              {group.icon && <DynamicIcon name={group.icon} className="h-4 w-4" />}
              {group.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Multi-select status chips. */
const StatusFilter: React.FC<{
  filters: RoleFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">
        {Object.entries(ROLE_STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const isActive = filters.statuses.includes(key as any);
          const color = config.badgeColor || 'rgb(var(--status-success))';
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': color } as React.CSSProperties}
              onClick={() => onToggleStatus(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Operational-mode chip filter. */
const ModeFilter: React.FC<{
  filters: RoleFilters;
  onToggleMode: (mode: string) => void;
}> = ({ filters, onToggleMode }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Modes opérationnels</span>
      <div className="filter-options">
        {Object.entries(ROLE_MODE_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const isActive = filters.modes.includes(key as any);
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleMode(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Role name search. */
const SearchFilter: React.FC<{
  filters: RoleFilters;
  onSetSearchTerm: (term: string) => void;
}> = ({ filters, onSetSearchTerm }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Recherche</span>
      <div className="relative">
        <SearchInput
          size="sm"
          shortcut={false}
          placeholder="Rechercher un rôle..."
          value={filters.searchTerm ?? ''}
          onChange={(e) => onSetSearchTerm(e.target.value || undefined)}
          onClear={() => onSetSearchTerm(undefined)}
        />
      </div>
    </div>
  );
};

/** Min/max hierarchy level filter. */
const HierarchyRangeFilter: React.FC<{
  filters: RoleFilters;
  onSetHierarchyRange: (min: number | undefined, max: number | undefined) => void;
}> = ({ filters, onSetHierarchyRange }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Niveaux hiérarchiques</span>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputSize="sm"
          placeholder="Min"
          className="w-20"
          value={filters.minHierarchyLevel ?? ''}
          onChange={(e) => {
            const val = e.target.value ? Number.parseInt(e.target.value) : undefined;
            onSetHierarchyRange(val, filters.maxHierarchyLevel);
          }}
          min={1}
          max={10}
        />
        <span className="text-sm text-muted-foreground">à</span>
        <Input
          type="number"
          inputSize="sm"
          placeholder="Max"
          className="w-20"
          value={filters.maxHierarchyLevel ?? ''}
          onChange={(e) => {
            const val = e.target.value ? Number.parseInt(e.target.value) : undefined;
            onSetHierarchyRange(filters.minHierarchyLevel, val);
          }}
          min={1}
          max={10}
        />
      </div>
    </div>
  );
};

/** Filter panel for the roles {@link DataTable}. */
export const RoleFiltersContent: React.FC<RoleFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  roleGroups,
  onToggleRoleGroup,
  onToggleStatus,
  onToggleMode,
  onSetSearchTerm,
  onSetHierarchyRange,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-role-groups') && (
        <RoleGroupFilter
          filters={filters}
          roleGroups={roleGroups}
          onToggleRoleGroup={onToggleRoleGroup}
        />
      )}
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-modes') && <ModeFilter filters={filters} onToggleMode={onToggleMode} />}
      {show('filter-hierarchy') && (
        <HierarchyRangeFilter filters={filters} onSetHierarchyRange={onSetHierarchyRange} />
      )}
      {show('filter-search') && (
        <SearchFilter filters={filters} onSetSearchTerm={onSetSearchTerm} />
      )}
    </div>
  );
};
